import { useRef, useState, useCallback } from 'react';
import { useTranslation } from './useTranslations.js';
import { useErrorContext } from '../providers/ErrorContextProvider.js';
import { sanitizeCardType, sanitizeCardTypes } from '../services/braintreeService.js';
import { braintreeTracking } from '../services/diiService.js';
import { captureException } from '../services/sentryService.js';
import { useBraintreeHostedFields, braintreeTokenizeCard } from '../utils/braintreeUtils.js';

const getAllFieldsValidity = (fields) => Object.keys(fields).every((key) => fields[key].isValid);
const useBraintreeState = (onFatalError, containerType, storeInVault) => {
    //change before commit
    const isLocalEnv = true;
    const braintreeInstance = useRef();
    const hostedFieldsInstance = useRef();
    const { t } = useTranslation();
    const { setError } = useErrorContext();
    const [intialized, setInitialized] = useState(false);
    const [fieldsValidity, setFieldsValidity] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [fieldsEmpty, setFieldsEmpty] = useState({});
    const [supportedCardTypes, setSupportedCardTypes] = useState([]);
    const [hostedFieldsCards, setHostedFieldsCards] = useState([]);
    const makeHostedFields = useBraintreeHostedFields(braintreeInstance);
    const updateValidity = (newFlagByFieldName) => {
        setFieldsValidity((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
    };
    const updateEmpty = (newFlagByFieldName) => {
        setFieldsEmpty((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
    };
    // Since braintree validity does not take supported card types into account
    // (a card can be of an ussuported type but considered valid anyway)
    // We need a manual validation of card type
    const isCurrentCardNumberSupported = useCallback(() => {
        return (!intialized ||
            !!hostedFieldsCards.find((card) => supportedCardTypes.includes(sanitizeCardType(card.type))));
    }, [intialized, hostedFieldsCards, supportedCardTypes]);
    const handleFatalError = useCallback((error) => {
        onFatalError?.(error);
        setError({
            message: t('checkout.payment.creditcard.error.method.unavailable'),
            retryable: false,
        });
    }, [onFatalError, t, setError]);
    const registerHostedFieldsCallbacks = useCallback(() => {
        hostedFieldsInstance.current?.on('validityChange', (event) => {
            console.log('Card validity changed :', event.fields);
            updateValidity({ [event.emittedBy]: event.fields[event.emittedBy]?.isValid });
            setIsValid(getAllFieldsValidity(event.fields));
        });
        hostedFieldsInstance.current?.on('empty', (event) => {
            console.log('Card field empty changed :', event.fields);
            updateEmpty({ [event.emittedBy]: event.fields[event.emittedBy]?.isEmpty });
        });
        hostedFieldsInstance.current?.on('notEmpty', (event) => {
            console.log('Card field empty changed :', event.fields);
            updateEmpty({ [event.emittedBy]: event.fields[event.emittedBy]?.isEmpty });
        });
        hostedFieldsInstance.current?.on('cardTypeChange', (event) => {
            console.log('Card type changed :', event.cards);
            setHostedFieldsCards(event.cards);
        });
    }, []);
    const createHostedFields = useCallback(async () => {
        console.log('Creating new hosted fields instance...');
        try {
            hostedFieldsInstance.current = await makeHostedFields();
            if (isLocalEnv)
                console.log('Braintree hosted fields instance created.');
            const state = hostedFieldsInstance.current.getState();
            setHostedFieldsCards(state.cards);
            setIsValid(getAllFieldsValidity(state.fields));
            const types = await hostedFieldsInstance.current.getSupportedCardTypes();
            if (isLocalEnv)
                console.log('Supported card nice types', types);
            const cardTypes = sanitizeCardTypes(types);
            if (isLocalEnv)
                console.log('Supported card types', cardTypes);
            setSupportedCardTypes(cardTypes);
            registerHostedFieldsCallbacks();
            setInitialized(true);
        }
        catch (error) {
            switch (error.code) {
                case 'HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME':
                    braintreeTracking('BTformKO', error.message);
                    break;
                default:
                    captureException({ error, message: 'Braintree hosted fields cannot be created' });
                    handleFatalError(error);
                    break;
            }
        }
    }, [t, registerHostedFieldsCallbacks, handleFatalError]);
    const tokenizeCard = useCallback(braintreeTokenizeCard({
        setError,
        hostedFieldsInstance,
        isCurrentCardNumberSupported,
        updateValidity,
        updateEmpty,
        isLocalEnv,
        storeInVault,
        containerType,
    }), [isCurrentCardNumberSupported, setError, storeInVault, containerType]);
    return {
        braintreeInstance,
        intialized,
        fieldsValidity,
        isValid,
        fieldsEmpty,
        supportedCardTypes,
        hostedFieldsCards,
        isCurrentCardNumberSupported,
        createHostedFields,
        tokenizeCard,
        handleFatalError,
    };
};

export { useBraintreeState as default };
//# sourceMappingURL=useBraintreeState.js.map
